import { useCallback } from 'react'
import { SensitiveIcon } from '@/components/icons'
import { confirmSensitiveImage } from '@/hooks'
import { cn } from '@/utils/cn'

/**
 * 敏感图片的遮罩：图本身糊掉并压暗，上面盖一层「敏感内容 / 确认显示」。
 *
 * 哪些图要遮由**后端**说了算：资产行上的 `sensitive`（后端迁移 0037），
 * 经 `PostSummary.cover_sensitive`、`PublicPostDetail.gallery[].sensitive` 等字段
 * 送到前端。前端不猜、也不按标签或分级推断。
 *
 * ## 为什么是「类 + 覆盖层」，而不是一个包装组件
 *
 * 站内每个出图的地方版位都不一样：卡片封面套在一个 `after:inset-0` 的整卡链接
 * 里、图集主图自己就是一个 `<a target="_blank">`、缩略图是 `<button>`、正文插图
 * 干脆是 `dangerouslySetInnerHTML` 出来的原生 DOM。一个 `<SensitiveImage>` 想同时
 * 吃下这四种，最后只会变成一堆 `variant` 开关。
 *
 * 所以这里只给两样东西，各处自己拼：
 *
 *   - `sensitiveImageClass` —— 加在 `<img>` 上的滤镜；
 *   - `<SensitiveOverlay>` —— 盖在同一个 `relative` 容器里的那一层。
 *
 * **覆盖层必须是链接/按钮的兄弟节点，不能放进它们里面。** 一来
 * `<button>` 套在 `<a>` 里不是合法 HTML，二来放进去之后点「确认显示」会连带
 * 触发外面那一层的跳转 —— 而人在那一刻想要的只是把图看清楚。
 *
 * 滤镜本身（模糊半径、压暗、放大多少）在 `hooks/useSensitiveMask.ts` 的
 * `sensitiveImageClass` 里，那里逐条写了每个值的理由。
 */

export interface SensitiveOverlayProps {
  /** 被遮的那张图的 URL，点「确认显示」后按它解锁。 */
  url: string
  /**
   * 紧凑版：只留图标与按钮，不写「敏感内容」四个字。
   *
   * 收藏列表的缩略图只有 56px 见方，图集缩略图也只有几十像素高 ——
   * 三行内容在那里会挤成一团糊字。
   */
  compact?: boolean
  className?: string
}

/**
 * 盖在图片上的那一层。放进与 `<img>` 同一个 `relative` 容器里。
 *
 * **整层就是那个按钮**，而不是「一层 div 里面放一个小按钮」：被遮的图往往只有
 * 几十像素，一个真正的小按钮既难点又难放。整层可点之后，那块白底黑字的
 * 「确认显示」只是它的标签，点哪儿都算数。
 */
export function SensitiveOverlay({ url, compact = false, className }: SensitiveOverlayProps) {
  const confirm = useCallback(
    (event: React.MouseEvent) => {
      // 外面往往还套着整卡链接或「打开原图」的 <a>：点这一层只表示
      // 「让我看看这张图」，不表示要跳转。
      event.preventDefault()
      event.stopPropagation()
      confirmSensitiveImage(url)
    },
    [url],
  )

  return (
    <button
      type="button"
      onClick={confirm}
      aria-label="敏感内容，确认显示"
      className={cn(
        'absolute inset-0 z-10 flex flex-col items-center justify-center bg-navy/50 text-center',
        compact ? 'gap-1 p-1' : 'gap-2 p-2',
        className,
      )}
    >
      <SensitiveIcon
        className={cn('text-navy-foreground', compact ? 'size-4' : 'size-7')}
        aria-hidden="true"
      />
      {!compact && <span className="text-sm font-medium text-navy-foreground">敏感内容</span>}
      {/*
        白底黑字：这一层的底色是一张被压暗的图，颜色不确定，
        只有反过来给它一块实心浅底，才在任何一张图上都读得清。
      */}
      <span
        className={cn(
          'rounded-md bg-navy-foreground font-bold text-navy',
          compact ? 'px-1.5 py-0.5 text-[0.625rem] leading-tight' : 'px-3 py-1 text-xs',
        )}
      >
        确认显示
      </span>
    </button>
  )
}
